import React from 'react';
import { useTimezone } from '@app/contexts/useTimezone';
import { DeviationHeatmapProps } from './types';
import { getDeviationColorIntensity, getDeviationTextColor } from './utils';

interface DeviationHeatmapViewProps extends DeviationHeatmapProps {
  dailyMeans?: { [date: string]: number };
  timestepMeans?: { [timestep: string]: number };
}

const DeviationHeatmapView: React.FC<DeviationHeatmapViewProps> = ({
  deviationData,
  dates,
  timesteps,
  aggregationMode,
  onAggregationModeChange,
  alignmentMode = 'native',
  onAlignmentModeChange,
  onCellHover,
  onCellLeave,
  dailyMeans = {},
  timestepMeans = {},
  statisticalContext,
}) => {
  const { timezone, formatInUserTimezone } = useTimezone();
  // Helper function to format date/timestep for display
  // Note: Timestamps are already converted to user timezone in data processing
  const formatDateOrTimestepForDisplay = (
    dateOrTimestep: string,
  ): { line1: string; line2?: string } => {
    const iso = new Date(dateOrTimestep).toISOString();
    if (aggregationMode === 'daily') {
      // For daily mode, just show date
      if (timezone === 'UTC') {
        return {
          line1: new Date(dateOrTimestep).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC',
          }),
        };
      }
      if (formatInUserTimezone) {
        return { line1: formatInUserTimezone(iso, 'MMM d') };
      }
      return {
        line1: new Date(dateOrTimestep).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
      };
    } else {
      // For hourly mode, show date on first line and time on second line
      if (timezone === 'UTC') {
        const datePart = new Date(dateOrTimestep).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        });
        const timePart = new Date(dateOrTimestep)
          .toLocaleTimeString('en-US', {
            hour: 'numeric',
            hour12: true,
            timeZone: 'UTC',
          })
          .toLowerCase();
        return { line1: datePart, line2: timePart };
      }
      if (formatInUserTimezone) {
        const datePart = formatInUserTimezone(iso, 'MMM d');
        const timePart = formatInUserTimezone(iso, 'h a').toLowerCase();
        return { line1: datePart, line2: timePart };
      }
      const datePart = new Date(dateOrTimestep).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      const timePart = new Date(dateOrTimestep)
        .toLocaleTimeString('en-US', {
          hour: 'numeric',
          hour12: true,
        })
        .toLowerCase();
      return { line1: datePart, line2: timePart };
    }
  };

  // Helper function to get actual and mean values for tooltip
  const getValuesForTooltip = (
    _modelLabel: string,
    dateOrTimestep: string,
    deviation: number | null,
  ) => {
    // Use the appropriate mean based on aggregation mode
    const meanValue =
      aggregationMode === 'daily'
        ? dailyMeans[dateOrTimestep] || 15 // Daily mean
        : timestepMeans[dateOrTimestep] || 15; // Timestep mean
    const actualValue = deviation !== null ? meanValue + deviation : null;
    return { actualValue, meanValue };
  };

  // Get the appropriate data array and headers based on aggregation mode
  const dataHeaders = aggregationMode === 'daily' ? dates : timesteps;
  const getDeviationValue = (rowData: (typeof deviationData)[0], key: string) => {
    return aggregationMode === 'daily'
      ? rowData.dailyDeviations[key]
      : rowData.timestepDeviations[key];
  };

  // Compute filtered headers if alignmentMode is common-only
  const filteredHeaders = React.useMemo(() => {
    if (alignmentMode !== 'common') return null;
    if (aggregationMode === 'daily') {
      return (dates || []).filter((d) =>
        deviationData.every(
          (row) => row.dailyDeviations[d] !== null && row.dailyDeviations[d] !== undefined,
        ),
      );
    }
    return (timesteps || []).filter((t) =>
      deviationData.every(
        (row) => row.timestepDeviations[t] !== null && row.timestepDeviations[t] !== undefined,
      ),
    );
  }, [alignmentMode, aggregationMode, dates, timesteps, deviationData]);

  if (deviationData.length === 0 || dates.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-gray-400">
        <p>No deviation data available</p>
        <p className="text-sm mt-1">Select models and wait for data to load completely</p>
      </div>
    );
  }

  return (
    <div className="p-2">
      <div className="space-y-2">
        {/* Aggregation Toggle */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
              Aggregation
            </label>
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-md">
              <button
                onClick={() => onAggregationModeChange('hourly')}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  aggregationMode === 'hourly'
                    ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                Hourly
              </button>
              <button
                onClick={() => onAggregationModeChange('daily')}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  aggregationMode === 'daily'
                    ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
              >
                Daily
              </button>
            </div>
          </div>
          {/* Alignment Toggle (hourly only) */}
          {aggregationMode === 'hourly' && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                Alignment
              </label>
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-md">
                <button
                  onClick={() => onAlignmentModeChange && onAlignmentModeChange('native')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                    alignmentMode === 'native'
                      ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                >
                  Native
                </button>
                <button
                  onClick={() => onAlignmentModeChange && onAlignmentModeChange('common')}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                    alignmentMode === 'common'
                      ? 'bg-gray-900 dark:bg-gray-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                >
                  Common only
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Scrollable table container with sticky model labels */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="flex">
            {/* Sticky column for model labels */}
            <div className="w-40 flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 z-10">
              {/* Sticky header for model labels */}
              <div className="bg-gray-50 dark:bg-gray-800 px-2 py-1 h-10 flex items-center border-b border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-600 dark:text-gray-300 font-medium">Models</div>
              </div>

              {/* Model labels */}
              {deviationData.map((rowData, index) => (
                <div
                  key={rowData.modelLabel}
                  className={`flex items-center px-2 border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${index % 2 === 0 ? 'bg-gray-25 dark:bg-gray-850' : 'bg-white dark:bg-gray-900'}`}
                  style={{ height: '40px' }}
                >
                  <div className="text-xs text-gray-600 dark:text-gray-300 font-medium leading-tight">
                    {rowData.modelLabel.includes(' | ') ? (
                      rowData.modelLabel.split(' | ').map((part, partIndex) => (
                        <div key={partIndex} className="break-words">
                          {part}
                        </div>
                      ))
                    ) : (
                      <div className="break-words">{rowData.modelLabel}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Scrollable content area (header + data together) */}
            <div
              className="flex-1 overflow-x-auto overflow-y-visible pb-4 pr-4"
              style={{ scrollbarGutter: 'stable' }}
            >
              <div className="min-w-max">
                {/* Column headers (Dates/Timesteps) */}
                <div className="flex items-center gap-0.5 bg-gray-50 dark:bg-gray-800 px-2 py-1 border-b border-gray-200 dark:border-gray-700 h-10">
                  {(filteredHeaders || dataHeaders).map((header) => {
                    const formatted = formatDateOrTimestepForDisplay(header);
                    const columnWidth = '50px';

                    return (
                      <div
                        key={header}
                        className="flex-none text-center h-full flex items-center justify-center"
                        style={{ minWidth: columnWidth, width: columnWidth }}
                      >
                        <div
                          className={`text-gray-600 dark:text-gray-300 font-medium ${formatted.line2 ? 'text-[8px]' : 'text-[10px]'}`}
                        >
                          <div className="whitespace-nowrap leading-tight">{formatted.line1}</div>
                          {formatted.line2 && (
                            <div className="whitespace-nowrap leading-tight">{formatted.line2}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Data rows */}
                {deviationData.map((rowData, index) => (
                  <div
                    key={rowData.modelLabel}
                    className={`flex items-center gap-0.5 px-2 border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${index % 2 === 0 ? 'bg-gray-25 dark:bg-gray-850' : 'bg-white dark:bg-gray-900'}`}
                    style={{ height: '40px' }}
                  >
                    {(filteredHeaders || dataHeaders).map((header) => {
                      const deviation = getDeviationValue(rowData, header);
                      const { actualValue, meanValue } = getValuesForTooltip(
                        rowData.modelLabel,
                        header,
                        deviation,
                      );
                      const columnWidth = '50px';

                      return (
                        <div
                          key={header}
                          className={`flex-none h-6 flex items-center justify-center rounded text-xs font-semibold cursor-pointer transition-all hover:scale-105 hover:shadow-md ${getDeviationColorIntensity(deviation, statisticalContext)} ${getDeviationTextColor(deviation)}`}
                          onMouseEnter={(e) => {
                            if (deviation !== null && actualValue !== null) {
                              onCellHover(
                                e,
                                rowData.modelLabel,
                                header,
                                deviation,
                                actualValue,
                                meanValue,
                              );
                            }
                          }}
                          onMouseLeave={onCellLeave}
                          style={{ minHeight: '24px', width: columnWidth }}
                        >
                          <span className="px-0.5 text-center">
                            {deviation === null
                              ? 'N/A'
                              : deviation === 0
                                ? '0.0'
                                : `${deviation > 0 ? '+' : ''}${deviation.toFixed(1)}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
            <span className="flex-shrink-0 font-medium">Below mean</span>
            <div className="flex-1 mx-4 h-3 rounded-full bg-gradient-to-r from-blue-400 via-blue-200 via-gray-100 via-pink-200 to-pink-400 dark:from-blue-600/90 dark:via-blue-900/60 dark:via-gray-700 dark:via-pink-900/60 dark:to-pink-600/90 border border-gray-300 dark:border-gray-600"></div>
            <span className="flex-shrink-0 font-medium">Above mean</span>
          </div>
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>-3+</span>
            <span>-1</span>
            <span>0</span>
            <span>+1</span>
            <span>+3+</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviationHeatmapView;
